import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ProviderPageLayout from '../../components/ProviderPageLayout';
import { BillingTermsSection } from '../../components/BillingTermsSection';

const headingStyle = { marginTop: 28, marginBottom: 10 };
const paragraphStyle = { marginBottom: 12 };

const ProviderTerms = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const cameFromSignup = location.state?.from === '/provider/signup';

  return (
    <ProviderPageLayout variant="auth" bare>
      <div className="provider-legal">
        <div className="provider-legal-header">
          <p className="provider-eyebrow">Legal</p>
          <h1 className="provider-title">Terms of Service</h1>
          <p className="provider-legal-updated">Last updated: {new Date().toLocaleDateString()}</p>
        </div>

        <div className="provider-legal-body">
          <p>
            These Terms of Service (&quot;Terms&quot;) govern your access to and use of the ReTechSys
            Provider Portal and related services (the &quot;Service&quot;) operated by RetechSys LLC
            (&quot;ReTechSys,&quot; &quot;we,&quot; &quot;us&quot;). By creating an account or using
            the Service, you agree to these Terms.
          </p>

          <h2 style={headingStyle}>1. Eligibility &amp; Accounts</h2>
          <p style={paragraphStyle}>
            You must be authorized to act on behalf of your business to register. You are responsible
            for maintaining the confidentiality of your account credentials and for all activity
            under your account.
          </p>

          <h2 style={headingStyle}>2. Use of the Service</h2>
          <p style={paragraphStyle}>
            We grant you a limited, non-exclusive, non-transferable right to access the Service for
            your business operations. You agree not to misuse, reverse engineer, or attempt to gain
            unauthorized access to the Service.
          </p>

          <h2 style={headingStyle}>3. Device Provisioning &amp; Ownership</h2>
          <p style={paragraphStyle}>
            Access is linked to a device provided or authorized by ReTechSys. You are responsible for
            keeping your device credentials secure. All devices remain the exclusive property of
            RetechSys LLC at all times unless expressly agreed otherwise in writing; providers are
            granted a right of use, not ownership.
          </p>
          <p style={paragraphStyle}>
            Devices are placed with providers with the expectation of reasonable, active use.
            ReTechSys reserves the right, upon reasonable notice, to reclaim, relocate, or redeploy
            any device that remains materially underutilized or inactive for an extended period, so
            that equipment can serve locations where it delivers the most value. You agree to
            provide reasonable access for the retrieval of such devices, and to return devices in
            good working condition, normal wear and tear excepted.
          </p>

          <h2 style={headingStyle}>4. Service Suspension &amp; Reactivation</h2>
          <p style={paragraphStyle}>
            You may request to stop service at any time through the Provider Portal. Upon a stop
            request, your device is deactivated and no new usage accrues; usage accrued prior to the
            stop remains payable in accordance with these Terms. Your account and transaction history
            remain accessible while service is stopped.
          </p>
          <p style={paragraphStyle}>
            Resuming service after a stop is subject to a <strong>reactivation fee of $50</strong>,
            which covers an on-site visit by a ReTechSys sales associate to recommission the device
            and confirm proper operation. Our team will coordinate the visit and the fee with you
            when you request reactivation.
          </p>

          <BillingTermsSection headingStyle={headingStyle} paragraphStyle={paragraphStyle} />

          <h2 style={headingStyle}>7. Service Availability</h2>
          <p style={paragraphStyle}>
            We strive to keep the Service available but do not guarantee uninterrupted access. We may
            modify or discontinue features with reasonable notice.
          </p>

          <h2 style={headingStyle}>8. Limitation of Liability</h2>
          <p style={paragraphStyle}>
            To the maximum extent permitted by law, ReTechSys is not liable for indirect, incidental,
            or consequential damages arising from your use of the Service. Our total liability shall
            not exceed the amounts paid by you in the three months preceding the claim.
          </p>

          <h2 style={headingStyle}>9. Termination</h2>
          <p style={paragraphStyle}>
            We may suspend or terminate access for non-payment, misuse, or violation of these Terms.
            You may close your account at any time by contacting support.
          </p>

          <h2 style={headingStyle}>10. Changes to These Terms</h2>
          <p style={paragraphStyle}>
            We may update these Terms from time to time. Material changes will be communicated with
            reasonable notice. Continued use after changes take effect constitutes acceptance.
          </p>

          <h2 style={headingStyle}>11. Contact</h2>
          <p style={paragraphStyle}>
            Questions about these Terms? Email{' '}
            <a href="mailto:support@retechsys.ai">support@retechsys.ai</a>.
          </p>
        </div>

        <div style={{ marginTop: 40 }}>
          <button
            type="button"
            onClick={() => navigate(cameFromSignup ? '/provider/signup' : '/provider/login')}
            className="provider-btn provider-btn--secondary provider-btn--inline"
          >
            ← {cameFromSignup ? 'Back to signup' : 'Back'}
          </button>
        </div>
      </div>
    </ProviderPageLayout>
  );
};

export default ProviderTerms;
